import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import type { FollowUpActionFileDto } from "@/generated/model";
import {
  cachedUserId,
  generateClientId,
  offlineDb,
  type OfflineFollowUpCompletion,
  type OfflineFollowUpPhoto,
  requireCachedUserId,
} from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

export interface DisplayFollowUpPhoto {
  id: string;
  fileName: string;
  mimeType: string;
  syncState: "pending" | "synced" | "error";
  // Local photos have blobUrl, server photos have downloadUrl
  blobUrl?: string;
  downloadUrl?: string;
  uploadStatus?: string;
  // Server-side file ID (for deduplication)
  serverId?: string;
}

interface OfflineFollowUpState {
  notes: string;
  completion: OfflineFollowUpCompletion | null;
  localPhotos: DisplayFollowUpPhoto[];
  syncStatus: SyncStatus;
}

export function useOfflineFollowUpActionCompletion(
  actionId: string,
  existingFiles?: FollowUpActionFileDto[],
  existingNotes?: string,
) {
  const [state, setState] = useState<OfflineFollowUpState>({
    notes: existingNotes || "",
    completion: null,
    localPhotos: [],
    syncStatus: {
      pendingCount: 0,
      errorCount: 0,
      isSyncing: false,
      isOnline: navigator.onLine,
    },
  });

  // Track blob URLs for cleanup
  const blobUrlsRef = useRef<Set<string>>(new Set());

  // Load existing offline data and merge with server state
  useEffect(() => {
    async function loadOfflineData() {
      const completion = await offlineDb.followUpCompletions.get(actionId);
      const localPhotoRecords = await offlineDb.followUpPhotos
        .filter((p) => p.actionId === actionId)
        .toArray();

      // Convert local photos to display format with blob URLs
      const displayPhotos: DisplayFollowUpPhoto[] = localPhotoRecords.map(
        (p) => {
          const blob = new Blob([p.data], { type: p.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          blobUrlsRef.current.add(blobUrl);
          return {
            id: p.id,
            fileName: p.fileName,
            mimeType: p.mimeType,
            syncState: p.syncState,
            blobUrl,
            serverId: p.serverId,
          };
        },
      );

      const syncStatus = await syncService.getStatus();

      setState((prev) => ({
        ...prev,
        notes: completion?.completionNotes ?? existingNotes ?? "",
        completion: completion || null,
        localPhotos: displayPhotos,
        syncStatus,
      }));
    }

    loadOfflineData();

    // Subscribe to sync status updates
    const unsubscribe = syncService.subscribe((syncStatus) => {
      setState((prev) => ({ ...prev, syncStatus }));
    });

    const handleOnline = () => {
      setState((prev) => ({
        ...prev,
        syncStatus: { ...prev.syncStatus, isOnline: true },
      }));
    };

    const handleOffline = () => {
      setState((prev) => ({
        ...prev,
        syncStatus: { ...prev.syncStatus, isOnline: false },
      }));
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const blobUrls = blobUrlsRef.current;
    return () => {
      unsubscribe();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      // Revoke all blob URLs on unmount
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
      blobUrls.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId]);

  // Merge server files with local photos (deduplicate by serverId)
  const photos: DisplayFollowUpPhoto[] = useMemo(() => {
    const localServerIds = new Set(
      state.localPhotos.map((p) => p.serverId).filter(Boolean),
    );
    const localIds = new Set(state.localPhotos.map((p) => p.id));

    const serverPhotos: DisplayFollowUpPhoto[] = (existingFiles || [])
      .filter((f) => !localServerIds.has(f.id) && !localIds.has(f.id))
      .map((f) => ({
        id: f.id,
        fileName: f.fileName,
        mimeType: f.mimeType || "image/jpeg",
        syncState: "synced" as const,
        downloadUrl: f.downloadUrl,
        uploadStatus: f.uploadStatus,
      }));

    return [...serverPhotos, ...state.localPhotos];
  }, [existingFiles, state.localPhotos]);

  const isLocallyCompleted = state.completion?.completedAt != null;

  // Surfaces a failed local write instead of letting it vanish. IndexedDB
  // rejects on storage quota, which is reachable on a shared tablet that now
  // retains photo blobs indefinitely — and a draft that looks saved but was
  // never written is the silent loss this area exists to prevent.
  const reportWriteFailure = useCallback((writeError: unknown) => {
    console.error("[Offline] Failed to persist follow-up draft", writeError);
    toast.error(
      "Couldn't save that to this device. Check your connection and try again.",
    );
  }, []);

  const persistNotes = useCallback(
    async (notes: string) => {
      // Persist to IndexedDB completion record
      // Re-stamped on every write, not just on create. The row is keyed by
      // actionId with no user component, and logout now preserves it — so a
      // draft started by one practitioner can be picked up by whoever signs in
      // next. Stamping only on create left their work recorded as the first
      // practitioner's, which the privilege floor happily accepts between peers.
      const capturedByUserId = requireCachedUserId(await cachedUserId());

      // One transaction, for the same reason as the compliance hook: an
      // unserialised get-then-add lets two concurrent writes both miss the row
      // and race on add(), dropping the loser's patch.
      let created = false;
      await offlineDb.transaction(
        "rw",
        offlineDb.followUpCompletions,
        async () => {
          const existing = await offlineDb.followUpCompletions.get(actionId);
          if (existing) {
            await offlineDb.followUpCompletions.update(actionId, {
              completionNotes: notes,
              capturedByUserId,
            });
            return;
          }

          await offlineDb.followUpCompletions.add({
            id: actionId,
            completionNotes: notes,
            completedAt: null,
            capturedByUserId,
            syncState: "pending",
          });
          created = true;
        },
      );

      if (created) {
        setState((prev) => ({
          ...prev,
          completion: {
            id: actionId,
            completionNotes: notes,
            completedAt: null,
            syncState: "pending",
          },
        }));
      }
    },
    [actionId],
  );

  // Set notes - update React state + persist to IndexedDB
  const setNotes = useCallback(
    async (notes: string) => {
      setState((prev) => ({ ...prev, notes }));

      try {
        await persistNotes(notes);
      } catch (writeError) {
        // Fired from onChange, where nothing catches — so without this the
        // draft silently fails to persist while the textarea shows the text.
        reportWriteFailure(writeError);
      }
    },
    [persistNotes, reportWriteFailure],
  );

  // Save photo to IndexedDB + enqueue sync
  const savePhoto = useCallback(
    async (file: File) => {
      const arrayBuffer = await file.arrayBuffer();

      const photo: OfflineFollowUpPhoto = {
        id: generateClientId(),
        actionId,
        data: arrayBuffer,
        mimeType: file.type,
        fileName: file.name,
        syncState: "pending",
      };

      await offlineDb.followUpPhotos.add(photo);

      // Add to sync queue (priority 5)
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "followUpPhoto",
        entityId: photo.id,
        priority: 5,
        createdAt: Date.now(),
        attempts: 0,
      });

      // Create blob URL for preview
      const blob = new Blob([arrayBuffer], { type: file.type });
      const blobUrl = URL.createObjectURL(blob);
      blobUrlsRef.current.add(blobUrl);

      const displayPhoto: DisplayFollowUpPhoto = {
        id: photo.id,
        fileName: photo.fileName,
        mimeType: photo.mimeType,
        syncState: photo.syncState,
        blobUrl,
      };

      setState((prev) => ({
        ...prev,
        localPhotos: [...prev.localPhotos, displayPhoto],
      }));

      // Trigger debounced sync if online
      if (navigator.onLine) {
        syncService.debouncedSync();
      }
    },
    [actionId],
  );

  // Delete photo from IndexedDB + sync queue, revoke blob URL
  const deletePhoto = useCallback(async (photoId: string) => {
    await offlineDb.followUpPhotos.delete(photoId);
    await offlineDb.syncQueue.filter((q) => q.entityId === photoId).delete();

    setState((prev) => {
      const removed = prev.localPhotos.find((p) => p.id === photoId);
      if (removed?.blobUrl) {
        URL.revokeObjectURL(removed.blobUrl);
        blobUrlsRef.current.delete(removed.blobUrl);
      }
      return {
        ...prev,
        localPhotos: prev.localPhotos.filter((p) => p.id !== photoId),
      };
    });
  }, []);

  // Complete the action - persist notes + enqueue finalize
  const completeAction = useCallback(async () => {
    const completedAt = new Date().toISOString();

    // Ensure completion record exists with latest notes
    // See setNotes: the completer is whoever is signed in now, not whoever
    // happened to start the draft before an auto-logout.
    const capturedByUserId = requireCachedUserId(await cachedUserId());
    const existing = await offlineDb.followUpCompletions.get(actionId);
    if (existing) {
      await offlineDb.followUpCompletions.update(actionId, {
        completionNotes: state.notes,
        completedAt,
        capturedByUserId,
        syncState: "pending",
      });
    } else {
      await offlineDb.followUpCompletions.add({
        id: actionId,
        completionNotes: state.notes,
        completedAt,
        capturedByUserId,
        syncState: "pending",
      });
    }

    // Add followUpFinalize to sync queue (priority 6)
    await offlineDb.syncQueue.add({
      id: generateClientId(),
      type: "followUpFinalize",
      entityId: actionId,
      priority: 6,
      createdAt: Date.now(),
      attempts: 0,
    });

    setState((prev) => ({
      ...prev,
      completion: {
        id: actionId,
        completionNotes: prev.notes,
        completedAt,
        syncState: "pending",
      },
    }));

    // Trigger immediate sync (user is done)
    if (navigator.onLine) {
      syncService.immediateSync();
    }
  }, [actionId, state.notes]);

  // Retry failed sync items
  const retryFailed = useCallback(() => {
    syncService.retryFailed();
  }, []);

  return {
    notes: state.notes,
    setNotes,
    photos,
    syncStatus: state.syncStatus,
    isLocallyCompleted,
    savePhoto,
    deletePhoto,
    completeAction,
    retryFailed,
  };
}
